import { cn } from "@/lib/utils"
import { TestimonialCard, TestimonialAuthor } from "@/components/ui/testimonial-card"
import { useScrollAnimation } from "@/hooks/use-scroll-animation"
import { useState, useEffect, useRef, useCallback } from "react"

interface TestimonialsSectionProps {
  title: string
  description: string
  testimonials: Array<{
    author: TestimonialAuthor
    text: string
    href?: string
  }>
  className?: string
}

export function TestimonialsWithMarquee({ 
  title,
  description,
  testimonials,
  className 
}: TestimonialsSectionProps) {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Check for reduced motion preference
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const scrollToIndex = useCallback((index: number) => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const cardWidth = container.offsetWidth * 0.85 + 12; // 85vw + gap
    container.scrollTo({
      left: index * cardWidth,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }, [prefersReducedMotion]);

  // Auto-play functionality
  useEffect(() => {
    if (prefersReducedMotion || isPaused || !isVisible) {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
        autoPlayRef.current = null;
      }
      return;
    }

    autoPlayRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % testimonials.length;
        scrollToIndex(next);
        return next;
      });
    }, 4000);

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isPaused, isVisible, testimonials.length, scrollToIndex, prefersReducedMotion]);

  // Track scroll position to update active index
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const cardWidth = container.offsetWidth * 0.85 + 12;
      const scrollLeft = container.scrollLeft;
      const newIndex = Math.round(scrollLeft / cardWidth);
      if (newIndex !== activeIndex && newIndex >= 0 && newIndex < testimonials.length) {
        setActiveIndex(newIndex);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [activeIndex, testimonials.length]);

  // Pause on touch/interaction
  const handleTouchStart = () => setIsPaused(true);
  const handleTouchEnd = () => {
    // Resume after 3 seconds of no interaction
    setTimeout(() => setIsPaused(false), 3000);
  };

  // Click on indicator dot
  const handleDotClick = (index: number) => {
    setActiveIndex(index);
    scrollToIndex(index);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 3000);
  };

  return (
    <section 
      ref={sectionRef as React.RefObject<HTMLElement>}
      className={cn("py-12 md:py-24 relative overflow-hidden bg-background", className)}
    >
      {/* Grid pattern - matching About page */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,hsl(var(--muted-foreground)/0.08)_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Header Section - About page style with slide-in from left */}
        <div 
          className={cn(
            "mb-8 md:mb-16 transition-all duration-1000 ease-out",
            isVisible ? "translate-x-0 opacity-100 blur-0" : "-translate-x-12 opacity-0 blur-sm"
          )}
        >
          <div className="grid lg:grid-cols-2 gap-0 rounded-2xl md:rounded-3xl overflow-hidden border border-border/50">
            {/* Left Content Side */}
            <div className="relative p-6 md:p-12 lg:p-16 bg-gradient-to-br from-card via-card to-muted/30">
              {/* Decorative accent */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-primary/10 rounded-full blur-3xl hidden md:block" />
              
              <p className="text-xs md:text-sm font-mono tracking-widest uppercase mb-3 md:mb-4 text-muted-foreground">
                Community Voices
              </p>
              
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 md:mb-6 leading-tight">
                {title.split(' ').slice(0, 2).join(' ')} <span className="text-muted-foreground">{title.split(' ').slice(2).join(' ')}</span>
              </h2>
              
              <p className="text-sm md:text-lg text-muted-foreground leading-relaxed">
                {description}
              </p>
            </div>

            {/* Right Design Side - Diamond/Hexagon Theme (Different from About page circles) */}
            <div className="relative p-6 md:p-12 lg:p-16 bg-gradient-to-bl from-primary/10 via-muted/50 to-card flex items-center justify-center min-h-[200px] md:min-h-[250px] lg:min-h-0">
              {/* Decorative Pattern - Diagonal lines instead of dots */}
              <div className="absolute inset-0 bg-[repeating-linear-gradient(45deg,hsl(var(--primary)/0.05)_0px,hsl(var(--primary)/0.05)_1px,transparent_1px,transparent_20px)]" />
              
              {/* Floating geometric shapes - Diamonds and Hexagons */}
              <div className="absolute top-6 md:top-8 right-6 md:right-8 w-12 md:w-16 h-12 md:h-16 border-2 border-primary/30 rotate-45" />
              <div className="absolute bottom-8 md:bottom-12 left-6 md:left-8 w-10 md:w-14 h-10 md:h-14 border-2 border-primary/25 rotate-45" />
              <div className="absolute top-1/3 left-1/4 w-8 md:w-10 h-8 md:h-10 bg-primary/15 rotate-45" />
              <div className="absolute bottom-1/4 right-1/3 w-6 md:w-8 h-6 md:h-8 border border-primary/20 rotate-45" />
              
              {/* Central decorative element - Hexagon inspired */}
              <div className="relative z-10 flex flex-col items-center justify-center text-center">
                <div className="w-20 md:w-24 h-20 md:h-24 mb-4 md:mb-6 rotate-45 bg-gradient-to-br from-primary/30 to-primary/10 border border-primary/20 flex items-center justify-center backdrop-blur-sm">
                  <div className="w-10 md:w-12 h-10 md:h-12 bg-primary/40 flex items-center justify-center -rotate-45">
                    <div className="w-5 md:w-6 h-5 md:h-6 rotate-45 bg-primary" />
                  </div>
                </div>
                <p className="text-xs md:text-sm font-mono text-primary/80 tracking-widest uppercase">Real Stories</p>
                <p className="text-[10px] md:text-xs text-muted-foreground mt-1 md:mt-2">From DePIN Community</p>
              </div>
              
              {/* Corner accents - Triangular */}
              <div className="absolute top-0 right-0 w-0 h-0 border-l-[50px] md:border-l-[80px] border-l-transparent border-t-[50px] md:border-t-[80px] border-t-primary/15" />
              <div className="absolute bottom-0 left-0 w-0 h-0 border-r-[40px] md:border-r-[60px] border-r-transparent border-b-[40px] md:border-b-[60px] border-b-primary/10" />
            </div>
          </div>
        </div>

        {/* Mobile: Horizontal Scrollable Carousel with Auto-play */}
        <div className="md:hidden -mx-4 px-4">
          <div 
            ref={scrollContainerRef}
            className="flex gap-3 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onMouseDown={handleTouchStart}
            onMouseUp={handleTouchEnd}
            onMouseLeave={handleTouchEnd}
          >
            {testimonials.map((testimonial, index) => (
              <div 
                key={index}
                className={cn(
                  "flex-shrink-0 w-[85vw] snap-center transition-all duration-700 ease-out",
                  isVisible ? "translate-y-0 scale-100 opacity-100 blur-0" : "translate-y-10 scale-95 opacity-0 blur-sm"
                )}
                style={{ transitionDelay: isVisible ? `${400 + index * 80}ms` : "0ms" }}
              >
                <TestimonialCard {...testimonial} />
              </div>
            ))}
          </div>
          {/* Active indicator dots */}
          <div className="flex justify-center gap-2 mt-2">
            {testimonials.map((_, index) => (
              <button 
                key={index}
                onClick={() => handleDotClick(index)}
                aria-label={`Go to testimonial ${index + 1}`}
                className={cn(
                  "w-2 h-2 rounded-full transition-all duration-300",
                  index === activeIndex 
                    ? "bg-primary w-6" 
                    : "bg-muted-foreground/30 hover:bg-muted-foreground/50"
                )}
              />
            ))}
          </div>
        </div>

        {/* Desktop: Grid Layout */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index}
              className={cn(
                "transition-all duration-700 ease-out",
                isVisible ? "translate-y-0 scale-100 opacity-100 blur-0" : "translate-y-10 scale-95 opacity-0 blur-sm"
              )}
              style={{ transitionDelay: isVisible ? `${400 + index * 80}ms` : "0ms" }}
            >
              <TestimonialCard {...testimonial} />
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
